import { useEffect, useState } from 'react';

interface LiveStatusProps {
  updatedAt: number | null;
  /** Set when the latest background refresh failed; the figures shown are older. */
  error?: string;
  onRetry: () => void;
}

function ago(updatedAt: number, now: number) {
  const minutes = Math.floor((now - updatedAt) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
}

/** "Updated just now" with a live dot; turns amber with a retry when a refresh fails. */
export default function LiveStatus({ updatedAt, error, onRetry }: LiveStatusProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setNow(Date.now());
  }, [updatedAt]);

  if (updatedAt === null) return null;

  if (error) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500" role="status">
        <span className="h-2 w-2 rounded-full bg-secondary-500" aria-hidden="true" />
        Couldn't refresh · showing figures from {ago(updatedAt, now)}
        <button
          type="button"
          onClick={onRetry}
          className="font-medium text-primary-700 underline-offset-2 hover:underline cursor-pointer"
        >
          Retry
        </button>
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs text-foreground-500"
      role="status"
      title="The dashboard refreshes itself every 2 minutes"
    >
      <span className="relative flex h-2 w-2" aria-hidden="true">
        <span className="absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-75 animate-ping motion-reduce:animate-none" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-500" />
      </span>
      Live · updated {ago(updatedAt, now)}
    </span>
  );
}
